import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	Grid,
	Tooltip,
	Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useState } from "react";

import { useTranslation } from "react-i18next";
import deleteIcon from "../../../assets/icons/delete.png";
import { useSettings } from "../../../hooks/useSettings";

export const TemplateBox = ({ onClick, data, handleRemove }) => {
	const { settings } = useSettings();

	// backwards compatibility - ignore old options from vehicleTypeId
	const backwardsCompatibleData = {
		...data,
		vehicleTypeId: {
			data: data.vehicleTypeId.data.filter((val) => {
				return (
					settings.vehicleOptions.vehicleTypesOptions[val]?.name?.length > 0
				);
			}),
			color: data.vehicleTypeId.color,
		},
	};

	const {
		loadingAddressCity,
		loadingAddressCountry,
		loadingAddressZip,
		loadingDate,
		loadingTime,
		unloadingAddressCity,
		unloadingAddressCountry,
		unloadingAddressZip,
		unloadingDate,
		unloadingTime,
		customerPrice,
		ftl,
		length,
		weight,
		vehicleTypeId,
		vehicleBodyId,
		additionalRequirements,
	} = backwardsCompatibleData;

	return (
		<Box
			onClick={() => onClick(backwardsCompatibleData)}
			sx={{
				border: "1px solid #b2b2b2",
				p: "19px 10px 10px 10px",
				position: "relative",
				cursor: "pointer",
			}}
		>
			<Actions template={backwardsCompatibleData} handleRemove={handleRemove} />
			<Box>
				<Addresses
					loadingAddressCountry={loadingAddressCountry}
					loadingAddressCity={loadingAddressCity}
					loadingAddressZip={loadingAddressZip}
					loadingDate={loadingDate}
					loadingTime={loadingTime}
					unloadingAddressCountry={unloadingAddressCountry}
					unloadingAddressCity={unloadingAddressCity}
					unloadingAddressZip={unloadingAddressZip}
					unloadingDate={unloadingDate}
					unloadingTime={unloadingTime}
				/>
				<Box sx={{ mt: "10px", display: "flex", flexWrap: "wrap", gap: "4px" }}>
					<List
						customerPrice={customerPrice}
						ftl={ftl}
						weight={weight}
						length={length}
						vehicleTypeId={vehicleTypeId}
						vehicleBodyId={vehicleBodyId}
						additionalRequirements={additionalRequirements}
					/>
				</Box>
			</Box>
		</Box>
	);
};

const Addresses = ({
	loadingAddressCountry,
	loadingAddressCity,
	loadingAddressZip,
	loadingDate,
	loadingTime,
	unloadingAddressCountry,
	unloadingAddressCity,
	unloadingAddressZip,
	unloadingDate,
	unloadingTime,
}) => {
	return (
		<Box
			sx={{
				display: "flex",
				justifyContent: "space-between",
				flexWrap: "wrap",
				gap: "4px",
			}}
		>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					alignItems: "flex-start",
				}}
			>
				<Box
					sx={{
						display: "inline-block",
						fontSize: "12px",
						color: "rgba(0, 0, 0, 0.6)",
						background:
							loadingDate?.color === "#D6EEFF"
								? "transparent"
								: loadingDate?.color,
					}}
				>
					{loadingDate?.data ? loadingDate.data?.replaceAll(":", " - ") : "-"}
				</Box>
				<Box
					sx={{
						display: "inline-block",
						fontSize: "12px",
						color: "rgba(0, 0, 0, 0.6)",
						background:
							loadingTime?.color === "#D6EEFF"
								? "transparent"
								: loadingTime?.color,
					}}
				>
					{loadingTime?.data ? loadingTime.data?.replaceAll("-", " - ") : "-"}
				</Box>
				<Box sx={{ display: "flex", alignItems: "center" }}>
					<span
						className={`fi fi-${loadingAddressCountry.toLowerCase()} fis`}
						style={{
							marginRight: "4px",
							borderRadius: "50%",
							height: "19.2px",
							width: "19.2px",
						}}
					></span>
					{loadingAddressCountry} {loadingAddressZip} {loadingAddressCity}
				</Box>
			</Box>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					alignItems: "flex-start",
				}}
			>
				<Box sx={{ fontSize: "12px", color: "rgba(0, 0, 0, 0.6)" }}>
					{unloadingDate ? unloadingDate?.replaceAll(":", " - ") : "-"}
				</Box>
				<Box
					sx={{
						display: "inline-block",
						fontSize: "12px",
						color: "rgba(0, 0, 0, 0.6)",
						background:
							unloadingTime?.color === "#D6EEFF"
								? "transparent"
								: unloadingTime?.color,
					}}
				>
					{unloadingTime?.data
						? unloadingTime.data?.replaceAll("-", " - ")
						: "-"}
				</Box>
				<Box sx={{ display: "flex", alignItems: "center" }}>
					<span
						className={`fi fi-${unloadingAddressCountry.toLowerCase()} fis`}
						style={{
							marginRight: "4px",
							borderRadius: "50%",
							height: "19.2px",
							width: "19.2px",
						}}
					></span>
					{unloadingAddressCountry} {unloadingAddressZip} {unloadingAddressCity}
				</Box>
			</Box>
		</Box>
	);
};

const List = ({
	customerPrice,
	ftl,
	weight,
	length,
	vehicleTypeId,
	vehicleBodyId,
	additionalRequirements,
}) => {
	const { settings } = useSettings();

	// required
	const preparedVehicleTypes = vehicleTypeId.data
		.map((val) => settings.vehicleOptions.vehicleTypesOptions[val]?.name)
		.filter(Boolean);
	// required
	const preparedVehicleBodyTypes = vehicleBodyId.data
		.map((val) => settings.vehicleOptions.vehicleBodyTypesOptions[val])
		.filter(Boolean);
	// optional
	const preparedAdditionalRequirements =
		additionalRequirements &&
		additionalRequirements.length > 0 &&
		additionalRequirements.map(
			(val) => settings.vehicleOptions.additionalRequirementsOptions[val]
		);

	return (
		<>
			{ftl?.data && (
				<Box
					sx={{
						background: ftl.color || "#D6EEFF",
						color: "#7F7F7F",
						border: "none",
						padding: "4px 10px",
						fontSize: "11px",
						display: "inline-block",
						textTransform: "uppercase",
					}}
				>
					{ftl.data}
				</Box>
			)}
			{customerPrice > 0 && (
				<Box
					sx={{
						background: "#D6EEFF",
						color: "#7F7F7F",
						border: "none",
						padding: "4px 10px",
						fontSize: "11px",
						display: "inline-block",
					}}
				>
					{customerPrice} €
				</Box>
			)}
			{weight?.data > 0 && (
				<Box
					sx={{
						background: weight.color || "#D6EEFF",
						color: "#7F7F7F",
						border: "none",
						padding: "4px 10px",
						fontSize: "11px",
						display: "inline-block",
					}}
				>
					{weight.data} t
				</Box>
			)}
			{length?.data > 0 && (
				<Box
					sx={{
						background: length.color || "#D6EEFF",
						color: "#7F7F7F",
						border: "none",
						padding: "4px 10px",
						fontSize: "11px",
						display: "inline-block",
					}}
				>
					{length.data} m
				</Box>
			)}
			{preparedVehicleTypes &&
				preparedVehicleTypes.length > 0 &&
				preparedVehicleTypes.map((val) => {
					return (
						<Box
							key={val}
							sx={{
								background: vehicleTypeId?.color || "#D6EEFF",
								color: "#7F7F7F",
								border: "none",
								padding: "4px 10px",
								fontSize: "11px",
								display: "inline-block",
							}}
						>
							{val}
						</Box>
					);
				})}
			{preparedVehicleBodyTypes &&
				preparedVehicleBodyTypes.length > 0 &&
				preparedVehicleBodyTypes.map((val) => {
					return (
						<Box
							key={val}
							sx={{
								background: vehicleBodyId?.color || "#D6EEFF",
								color: "#7F7F7F",
								border: "none",
								padding: "4px 10px",
								fontSize: "11px",
								display: "inline-block",
							}}
						>
							{val}
						</Box>
					);
				})}
			{preparedAdditionalRequirements &&
				preparedAdditionalRequirements.length > 0 &&
				preparedAdditionalRequirements.map((val) => {
					return (
						<Box
							key={val}
							sx={{
								background: "#D6EEFF",
								color: "#7F7F7F",
								border: "none",
								padding: "4px 10px",
								fontSize: "11px",
								display: "inline-block",
							}}
						>
							{val}
						</Box>
					);
				})}
		</>
	);
};

const Actions = ({ template, handleRemove }) => {
	const { t } = useTranslation();
	const { id } = template;

	const { enqueueSnackbar } = useSnackbar();
	const [isRemoving, setIsRemoving] = useState(false);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

	const onRemove = (e) => {
		e.stopPropagation();
		setIsRemoving(true);
		handleRemove(id);
		setIsRemoving(false);
		setOpenDeleteDialog(false);
		enqueueSnackbar(t("removed_proposition"), { variant: "success" });
	};

	return (
		<Box
			onClick={(e) => e.stopPropagation()}
			sx={{
				position: "absolute",
				top: "-9px",
				right: "10px",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				gap: "4px",
			}}
		>
			<Tooltip
				title={
					<Typography variant="p" sx={{ fontSize: "9pt" }} component="span">
						{t("remove")}
					</Typography>
				}
				placement="bottom"
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						padding: "2px",
						border: "1px solid rgba(224, 224, 224, 1)",
						borderRadius: "4px",
						cursor: "pointer",
						height: "18px",
						width: "18px",
						background: "#fff",
					}}
					onClick={(e) => {
						e.stopPropagation();
						setOpenDeleteDialog(true);
					}}
				>
					{!isRemoving && (
						<img
							src={deleteIcon}
							alt="delete"
							style={{ width: "100%", height: "100%", objectFit: "contain" }}
						/>
					)}
					{isRemoving && <CircularProgress size={12} />}
				</Box>
			</Tooltip>
			<DeleteDialog
				open={openDeleteDialog}
				onClose={() => setOpenDeleteDialog(false)}
				handleRemove={onRemove}
				isLoading={isRemoving}
			/>
		</Box>
	);
};

const DeleteDialog = ({ open, onClose, handleRemove, isLoading }) => {
	const { t } = useTranslation();

	return (
		<Dialog
			fullWidth
			sx={{ maxWidth: "450px", mx: "auto" }}
			onClose={onClose}
			open={open}
		>
			<Box sx={{ padding: "30px" }}>
				<Grid container rowSpacing={3} columnSpacing={2}>
					<Grid size={12}>
						<Box sx={{ textAlign: "center" }}>
							<Typography variant="h5" component="span">
								{t("are_you_sure_remove")}
							</Typography>
						</Box>
					</Grid>
					<Grid size={6}>
						<Box sx={{ display: "flex", justifyContent: "end" }}>
							<Button
								onClick={handleRemove}
								loading={isLoading}
								variant="contained"
								color="error"
							>
								{t("remove")}
							</Button>
						</Box>
					</Grid>
					<Grid size={6}>
						<Box sx={{ display: "flex", justifyContent: "start" }}>
							<Button variant="contained" color="secondary" onClick={onClose}>
								{t("cancel")}
							</Button>
						</Box>
					</Grid>
				</Grid>
			</Box>
		</Dialog>
	);
};
